import React, { useEffect, useRef, useState, Fragment } from "react";
import { Helmet } from "react-helmet";
import { get } from "lodash";
import queryString from "query-string";
import { useLocation } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { Form } from "@unform/web";
import tvgConf from "@tvg/conf";

import { capitalizeFirstLetter, replaceAll } from "@tvg/utils/generalUtils";
import WelcomeRegistration from "@tvg/atomic-ui/_templates/WelcomeRegistration";
import RegularPageRegistration from "@tvg/atomic-ui/_templates/RegularPageRegistration";
import InputV2 from "@tvg/atomic-ui/_molecule/InputV2";
import InputMask from "@tvg/atomic-ui/_molecule/InputMask";
import { makeRange } from "@tvg/atomic-ui/_molecule/DateOfBirthSelect";
import Button from "@tvg/atomic-ui/_atom/Buttons/button";
import { fontMedium } from "@tvg/atomic-ui/_static/Typography";
import { REGISTRATION_STEPS } from "@tvg/atomic-ui/_static/Constants/progressBar";
import Icon from "@tvg/atomic-ui/_static/Icons";
import { eyeLineHide, eyeLineShow } from "@tvg/atomic-ui/_static/Icons/icons";
import buildColor from "@tvg/atomic-ui/_static/ColorPalette";
import RegListItem from "@tvg/atomic-ui/_molecule/RegistrationListItem";

import {
  handlePromoCodeModalRegistrationAction,
  setCurrentStepRegistrationAction
} from "../../actions";
import {
  handleInputOnBlurGtm,
  handleInputOnTheFlyChange
} from "../../utils/handlers";
import {
  getEnableRegAutoAddress,
  getInvalidPromo,
  getIsEditing,
  getLastStep,
  getPassThroughAddress,
  getPromoCodesMap,
  getReferAFriendRegistrationCode,
  getRegistrationAllPromosWithTC,
  getRegistrationTexts,
  getSelectedPromo,
  getWelcomeFields,
  getWelcomeIsFormValid
} from "../../utils/selectors";
import {
  birthSchema,
  firstNameSchema,
  handleSubmitFn,
  lastNameSchema,
  emailSchema,
  phoneSchema,
  passwordSchema,
  defaultListItems,
  handlePasswordChange
} from "./helpers";
import {
  onBackButtonClickGtmEvent,
  onClickAwayFromFormGtmEvent
} from "../../utils/gtm/otherInteractions";
import { onVirtualPageViewGtmEvent } from "../../utils/gtm/pageViews";
import useWelcomePromoSetup from "../../hooks/useWelcomePromoSetup";

const options = [];
const currentYear = new Date().getFullYear();
const arrayDays = makeRange(1, 31);
const arrayMonths = makeRange(1, 12);
const arrayYears = makeRange(currentYear - 100, currentYear - 18).reverse();
options.push(arrayMonths, arrayDays, arrayYears);

export const WelcomeComponent = ({
  device,
  isFormPristine,
  setIsFormPristine
}) => {
  const location = useLocation();
  const dispatch = useDispatch();

  const registrationTexts = useSelector(getRegistrationTexts);
  const promoCodesMap = useSelector(getPromoCodesMap);
  const referAFriendRegistrationCode = useSelector(
    getReferAFriendRegistrationCode
  );

  const welcomeFields = useSelector(getWelcomeFields);
  const isFormValid = useSelector(getWelcomeIsFormValid);
  const selectedPromo = useSelector(getSelectedPromo);
  const invalidPromo = useSelector(getInvalidPromo);
  const lastStep = useSelector(getLastStep);
  const isEditing = useSelector(getIsEditing);
  const passThroughAddress = useSelector(getPassThroughAddress);

  const registrationAllPromosWithTC = useSelector(
    getRegistrationAllPromosWithTC
  );
  const enableRegAutoAddress = useSelector(getEnableRegAutoAddress);

  const formRef = useRef(null);
  const [hasPreloadedInputs, setHasPreloadedInputs] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isFormDirty, setIsFormDirty] = useState(false);

  const [headerTitle, setHeaderTitle] = useState("");

  const [isWelcomeHeaderCollapsed, setIsWelcomeHeaderCollapsed] =
    useState(false);
  const welcomeHeaderTitleRef = useRef(null);

  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const [capitalListItem, setCapitalListItem] = useState(
    defaultListItems.capital
  );
  const [numberListItem, setNumberListItem] = useState(defaultListItems.number);
  const [charsListItem, setCharsListItem] = useState(defaultListItems.chars);

  useEffect(() => {
    if (get(registrationTexts, "welcome.pageTitle", "") && isLoading) {
      // Messages are loaded, we can hid loading mask
      setIsLoading(false);
    }
  }, [JSON.stringify(registrationTexts)]);

  useEffect(() => {
    if (isEditing) {
      onVirtualPageViewGtmEvent(location, "edit-personal-details");
    }
  }, [isEditing]);

  useWelcomePromoSetup({
    dispatch,
    selectedPromo,
    promoCodesMap,
    referAFriendRegistrationCode
  });

  useEffect(() => {
    let inputUpdate;

    if (!isLoading) {
      // Populate welcome fields for editing purposes
      const queryParams = queryString.parse(location.search);
      formRef.current.setData(
        !welcomeFields.password && queryParams.welcomeFields
          ? {
              ...JSON.parse(decodeURIComponent(queryParams.welcomeFields)),
              password: null
            }
          : welcomeFields
      );

      // In order to not send GTM events when going back and forward,
      // this is to prevent that and only dispatch GTM after we have updated inputs
      inputUpdate = setTimeout(() => {
        setHasPreloadedInputs(true);
      }, 0);

      handlePasswordChange(
        welcomeFields.password,
        capitalListItem,
        setCapitalListItem,
        numberListItem,
        setNumberListItem,
        charsListItem,
        setCharsListItem,
        false
      );

      if (device !== "mobile") {
        formRef.current.setFieldValue("birth", welcomeFields.birth.split(" "));
      }

      if (lastStep === REGISTRATION_STEPS.ADDRESS) {
        const birthField = document.getElementById("birth");
        if (birthField) {
          birthField.focus();
        }
      }
    }

    return () => {
      if (!isLoading) {
        clearTimeout(inputUpdate);
      }
    };
  }, [isLoading, JSON.stringify(welcomeFields)]);

  useEffect(() => {
    if (!isLoading) {
      handleInputOnTheFlyChange(
        isFormDirty,
        "password",
        formRef.current.getFieldValue("password"),
        passwordSchema({
          registrationTexts,
          capitalListItem,
          numberListItem,
          charsListItem
        }),
        formRef,
        registrationTexts.welcome.innerTitle,
        false
      );
    }
  }, [
    isLoading,
    isFormDirty,
    capitalListItem.status,
    numberListItem.status,
    charsListItem.status
  ]);

  // Handle mobile header scroll effect
  // eslint-disable-next-line consistent-return
  useEffect(() => {
    if (
      !isLoading &&
      welcomeHeaderTitleRef &&
      welcomeHeaderTitleRef.current &&
      device === "mobile"
    ) {
      const opts = {
        rootMargin: "0px",
        threshold: 1.0
      };

      let titleObserver;

      if ("IntersectionObserver" in window) {
        titleObserver = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            const isBeingIntersected = entry.isIntersecting;
            setIsWelcomeHeaderCollapsed(!isBeingIntersected);
          });
        }, opts);

        titleObserver.observe(welcomeHeaderTitleRef.current);
      }

      return () => {
        "IntersectionObserver" in window && welcomeHeaderTitleRef.current
          ? titleObserver.disconnect()
          : null;
      };
    }
  }, [isLoading, welcomeHeaderTitleRef]);

  useEffect(() => {
    if (selectedPromo && selectedPromo.code) {
      if (selectedPromo.code === "raf") {
        const refName = selectedPromo.promoReference.split("-")[1];
        setHeaderTitle(
          replaceAll(
            get(registrationTexts, "welcome.referralcode.pageTitle", ""),
            "@@@referralName@@@",
            capitalizeFirstLetter(refName)
          )
        );
      } else {
        setHeaderTitle(
          selectedPromo.title
            ? selectedPromo.title
            : registrationTexts.welcome.promocode.pageTitle
        );
      }
    } else {
      setHeaderTitle(registrationTexts.welcome.pageTitle);
    }
  }, [
    selectedPromo,
    get(registrationTexts, "welcome.promocode.pageTitle", ""),
    get(registrationTexts, "welcome.referralcode.pageTitle", "")
  ]);

  const formBody = (
    <Form
      ref={formRef}
      onSubmit={handleSubmitFn({
        setIsFormDirty,
        registrationTexts,
        formRef,
        dispatch,
        lastStep,
        isEditing,
        isFormValid,
        passThroughAddress,
        capitalListItem,
        setCapitalListItem,
        numberListItem,
        setNumberListItem,
        charsListItem,
        setCharsListItem,
        enableRegAutoAddress
      })}
    >
      <InputV2
        name="firstName"
        label="First Name"
        qaLabel="welcome-first-name"
        autoCompleteValue="given-name"
        hasPreloadedInputs={hasPreloadedInputs}
        debouncedValueCallback={(value) => {
          handleInputOnBlurGtm(
            "firstName",
            firstNameSchema({ registrationTexts }),
            isEditing,
            registrationTexts.welcome.innerTitle
          )(value);
        }}
        customHandleOnBlur={() => {
          if (isFormPristine) {
            setIsFormPristine(false);
          }
        }}
        customHandleChange={(value) => {
          handleInputOnTheFlyChange(
            isFormDirty,
            "firstName",
            value,
            firstNameSchema({ registrationTexts }),
            formRef,
            registrationTexts.welcome.innerTitle,
            isEditing
          );
        }}
        marginBottom={12}
      />
      <InputV2
        name="lastName"
        label="Last Name"
        autoCompleteValue="family-name"
        hasPreloadedInputs={hasPreloadedInputs}
        qaLabel="welcome-last-name"
        debouncedValueCallback={(value) => {
          handleInputOnBlurGtm(
            "lastName",
            lastNameSchema({ registrationTexts }),
            isEditing,
            registrationTexts.welcome.innerTitle
          )(value);
        }}
        customHandleOnBlur={() => {
          if (isFormPristine) {
            setIsFormPristine(false);
          }
        }}
        customHandleChange={(value) => {
          handleInputOnTheFlyChange(
            isFormDirty,
            "lastName",
            value,
            lastNameSchema({ registrationTexts }),
            formRef,
            registrationTexts.welcome.innerTitle,
            isEditing
          );
        }}
        marginBottom={12}
      />
      <InputMask
        name="birth"
        label="Date of Birth"
        qaLabel="welcome-birth"
        optionalLabel="MM DD YYYY"
        inputMode="numeric"
        {...{ autoComplete: "bday" }}
        hasPreloadedInputs={hasPreloadedInputs}
        debouncedValueCallback={(value) => {
          handleInputOnBlurGtm(
            "birth",
            birthSchema({ registrationTexts }),
            isEditing,
            registrationTexts.welcome.innerTitle
          )(value);
        }}
        letterSpacing={1}
        marginBottom={24}
        customHandleOnBlur={() => {
          if (isFormPristine) {
            setIsFormPristine(false);
          }
        }}
        customHandleChange={(value) => {
          handleInputOnTheFlyChange(
            isFormDirty,
            "birth",
            value,
            birthSchema({ registrationTexts }),
            formRef,
            registrationTexts.welcome.innerTitle,
            isEditing
          );
        }}
        mask="99 99 9999"
        placeholder="__ __ ____"
        alwaysShowMask
      />
      <InputV2
        name="email"
        label="Email"
        inputMode="email"
        qaLabel="contact-email"
        placeholder="johnsmith@mail.com"
        autoCompleteValue="email"
        hasPreloadedInputs={hasPreloadedInputs}
        debouncedValueCallback={(value) => {
          handleInputOnBlurGtm(
            "email",
            emailSchema({ registrationTexts }),
            isEditing,
            registrationTexts.welcome.innerTitle
          )(value);
        }}
        customHandleOnBlur={() => {
          if (isFormPristine) {
            setIsFormPristine(false);
          }
        }}
        customHandleChange={(value) => {
          handleInputOnTheFlyChange(
            isFormDirty,
            "email",
            value,
            emailSchema({ registrationTexts }),
            formRef,
            registrationTexts.welcome.innerTitle,
            isEditing
          );
        }}
        marginBottom={12}
        isLowerCase
      />
      <InputMask
        name="phone"
        label="Phone Number"
        qaLabel="contact-phone"
        inputMode="tel"
        mask="(999) 999-9999"
        maskChar="_"
        letterSpacing={1}
        placeholder="(555) 555-5555"
        {...{ autoComplete: "tel-national" }}
        hasPreloadedInputs={hasPreloadedInputs}
        debouncedValueCallback={(value) => {
          handleInputOnBlurGtm(
            "phone",
            phoneSchema({ registrationTexts }),
            isEditing,
            registrationTexts.welcome.innerTitle
          )(value);
        }}
        customHandleOnBlur={() => {
          if (isFormPristine) {
            setIsFormPristine(false);
          }
        }}
        customHandleChange={(value) => {
          handleInputOnTheFlyChange(
            isFormDirty,
            "phone",
            value,
            phoneSchema({ registrationTexts }),
            formRef,
            registrationTexts.welcome.innerTitle,
            isEditing
          );
        }}
        marginBottom={24}
      />
      {!isEditing && (
        <Fragment>
          <InputV2
            name="password"
            label="Password"
            autoCompleteValue="new-password"
            type={isPasswordVisible ? "text" : "password"}
            customHandleOnBlur={(value) => {
              if (isFormPristine) {
                setIsFormPristine(false);
              }

              handleInputOnBlurGtm(
                "password",
                passwordSchema({
                  registrationTexts,
                  capitalListItem,
                  numberListItem,
                  charsListItem
                }),
                false,
                registrationTexts.welcome.innerTitle
              )(value);
            }}
            customHandleChange={(value) => {
              handlePasswordChange(
                value,
                capitalListItem,
                setCapitalListItem,
                numberListItem,
                setNumberListItem,
                charsListItem,
                setCharsListItem,
                isFormDirty
              );
            }}
            qaLabel="password-password"
            placeholder="Password"
            icon={
              <Icon
                icon={isPasswordVisible ? eyeLineHide : eyeLineShow}
                size={16}
                viewBoxSize={16}
                color={buildColor("blue_accent", "500")}
              />
            }
            handleIconClick={() => setIsPasswordVisible(!isPasswordVisible)}
            marginBottom={16}
          />
          <RegListItem
            stepQaLabel="password"
            title="Your password must have:"
            list={[capitalListItem, numberListItem, charsListItem]}
          />
        </Fragment>
      )}
      <Button
        size="bigger"
        hasRoundedCorners
        isStretched
        isUppercase={false}
        hasShadow
        fontFamily={fontMedium}
        isBold={false}
        qaLabel="registration-submit-btn"
      >
        Continue
      </Button>
    </Form>
  );

  return (
    <Fragment>
      <Helmet>
        {enableRegAutoAddress &&
          typeof window !== "undefined" &&
          !get(window, "google.maps", false) && (
            <script
              type="text/javascript"
              async
              defer
              src={`https://maps.googleapis.com/maps/api/js?key=${tvgConf().config(
                "placesAPI"
              )}&libraries=places&callback=initMap`}
            />
          )}
      </Helmet>
      {isEditing ? (
        <RegularPageRegistration
          device={device}
          brand={tvgConf().brand}
          innerTitle={`Edit ${registrationTexts.welcome.innerTitle}`}
          infoText={registrationTexts.welcome.innerDescription}
          handleBackButton={() => {
            onBackButtonClickGtmEvent(
              `"Edit ${registrationTexts.welcome.innerTitle}`
            );
            dispatch(
              setCurrentStepRegistrationAction(REGISTRATION_STEPS.REVIEW)
            );
          }}
          currentStep={REGISTRATION_STEPS.REVIEW}
          registrationAllPromosWithTC={registrationAllPromosWithTC}
          isEditing={isEditing}
        >
          {formBody}
        </RegularPageRegistration>
      ) : (
        <WelcomeRegistration
          device={device}
          brand={tvgConf().brand}
          isLoading={isLoading}
          redirectText={
            device === "mobile"
              ? registrationTexts.welcome.mobileRedirectText
              : registrationTexts.welcome.redirectText
          }
          headerTitle={headerTitle}
          headerDescription={
            selectedPromo && selectedPromo.code
              ? selectedPromo.instructions
              : ""
          }
          innerTitle={registrationTexts.welcome.innerTitle}
          infoText={registrationTexts.welcome.innerDescription}
          promoCTAText={registrationTexts.welcome.promoCTA}
          hasPromo={selectedPromo && !!selectedPromo.code}
          hasInvalidPromo={invalidPromo}
          notFoundPromoText={registrationTexts.welcome.notFoundPromo}
          handleTermsAndDetails={() => {
            dispatch(handlePromoCodeModalRegistrationAction(true));
          }}
          handleClickAwayGtm={onClickAwayFromFormGtmEvent}
          bottomInfo={registrationTexts.review.passwordText}
          welcomeHeaderTitleRef={welcomeHeaderTitleRef}
          isWelcomeHeaderCollapsed={isWelcomeHeaderCollapsed}
        >
          {formBody}
        </WelcomeRegistration>
      )}
    </Fragment>
  );
};

export default WelcomeComponent;
